<template>
  <DetailLayout @close="$emit('close')">
    <!-- No `title` prop: the `#header` slot replaces DetailHeader's whole title
             area, which is the only thing `title` feeds. -->
    <template #header>
      <h2 class="raw-heading-2">
        {{ i18n(article.title) }}
      </h2>
    </template>

    <!-- Trailing header controls, supplied by the caller (the panel passes its
             extend button; the reader passes nothing — inside, only the ✕). -->
    <template v-if="$slots.actions" #actions>
      <slot name="actions" />
    </template>

    <article :key="article.id" class="grid gap-20">
      <!-- Article Main Image -->
      <ImageFigure :src="article.image" :caption="article.imageCaption" />

      <!-- Article Metas -->
      <ArticleMeta :article :zone />

      <!-- eslint-disable tailwindcss/no-custom-classname -->
      <em v-if="article.excerpt" data-standfirst class="normalFontSynthesis">
        {{ i18n(article.excerpt) }}
      </em>

      <hr
        class="h-[1px] border-0 bg-[black]/15 [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]"
      />

      <ArticleBody v-if="article.content" :content="article.content" />

      <ShareButton :zone class="justify-self-start" />

      <!-- Here rather than in the two adapters, so the panel and the reader get it
                 from one place. -->
      <TakeActionCallout :zone />

      <RelatedArticles :metal-slug="article.metal.slug" separator :zone />
    </article>
  </DetailLayout>
</template>

<script setup lang="ts">
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import DetailLayout from '~/scopes/campaign/components/UI/DetailLayout.vue'
import ImageFigure from '~/scopes/campaign/components/UI/ImageFigure.vue'
import ShareButton from '~/scopes/campaign/components/UI/ShareButton.vue'
import RelatedArticles from '~/scopes/campaign/components/highlights/metal/RelatedArticles.vue'
import ArticleBody from '~/scopes/campaign/components/impacts/article/ArticleBody.vue'
import ArticleMeta from '~/scopes/campaign/components/impacts/article/ArticleMeta.vue'
import TakeActionCallout from '~/scopes/campaign/components/takeAction/TakeActionCallout.vue'
import type { Article } from '~/scopes/campaign/data/articles/data'

// The whole article view — header included — shared verbatim by the narrow panel
// (`ArticleDetail`) and the extended reader (`ExtendedReader`). The reader isn't a
// variant of the article, it's the same article in a wider container, so this stays a
// dumb view: no `useModalState`, and deliberately no `trackPageView` (both callers can
// be mounted at once, so a page view here would fire twice — each adapter owns its own).
//
// The template's root element must stay alone — no sibling comment, nothing. A second
// root-level node makes the render root a fragment, and this view is a `<Transition>`
// child (ContentSwap): the leaving copy is then never removed from the DOM, so swapped
// articles pile up. Dev-only, since the production SFC build strips comments — which is
// precisely why it's easy to reintroduce. Hence this note living here, not there.
defineProps<{
  article: Article
  /** Tracking surface for the CTAs inside (`article-detail` / `article-extended`). */
  zone: string
}>()

defineEmits<{ close: [] }>()

const i18n = useI18n()
</script>

<style lang="css" scoped>
.normalFontSynthesis {
  font-synthesis: weight style small-caps;
}
</style>
